/**
 * sortResourceGroupsByPriority — order the Resource picker's grouped buckets
 * by domain priority. Pre-Sub-5 the buckets used `Map` insertion order which
 * mirrored the backend response order — backend currently lists Classroom
 * first, so the most-used Aircraft bucket was buried.
 *
 * Priority order (user-requested, browser smoke 2026-05-13):
 *   Aircraft → Simulator → Headphones → Classroom → other (alphabetical tiebreak)
 *
 * Comparison is case-insensitive and tolerant of plural / verbose backend
 * type names (e.g. `"Aircrafts"`, `"AIRCRAFT"`). Matching is substring-based
 * for a stable contract: the priority key is treated as the canonical
 * lowercase root, and any bucket title that starts with that root matches.
 *
 * Pure utility so the priority contract is testable (per
 * `feedback_test_scope.md` — utility-test allow-list). The component just
 * calls `sortResourceGroupsByPriority(groupsArray)` after building buckets.
 *
 * @module sortResourceGroupsByPriority
 */

// Roots are the shortest common prefixes that cover both singular and
// plural backend variants (`Aircraft`/`Aircrafts`, `Headphone`/`Headphones`,
// etc.). The match is `title.toLowerCase().startsWith(root)` so picking the
// singular form matches both.
const PRIORITY_ORDER = ['aircraft', 'simulator', 'headphone', 'classroom'];

/**
 * Map a bucket title to its priority index (0-based). Returns
 * `PRIORITY_ORDER.length` (= 4) for unknown types so they fall to the end.
 *
 * @param {string} title
 * @returns {number}
 */
export function priorityIndexForTitle(title) {
	if (typeof title !== 'string' || title.length === 0) {
		return PRIORITY_ORDER.length;
	}
	const lower = title.toLowerCase();
	for (let i = 0; i < PRIORITY_ORDER.length; i += 1) {
		// Prefix match so `"aircraft"` and `"aircrafts"` both rank as #0.
		if (lower.startsWith(PRIORITY_ORDER[i])) return i;
	}
	return PRIORITY_ORDER.length;
}

/**
 * Sort the bucket array in place. Returns the same array reference so the
 * caller can chain.
 *
 * @param {Array<{title: string, [k: string]: any}>} groups
 * @returns {Array} Sorted groups.
 */
export function sortResourceGroupsByPriority(groups) {
	if (!Array.isArray(groups)) return [];
	return [...groups].sort((a, b) => {
		const pa = priorityIndexForTitle(a?.title);
		const pb = priorityIndexForTitle(b?.title);
		if (pa !== pb) return pa - pb;
		// Tiebreak: alphabetical (case-insensitive). Unknown vs unknown is
		// stable by title; same-priority known types (e.g. user later adds a
		// second "Aircraft" subtype) stay alphabetical too.
		const ta = String(a?.title ?? '').toLowerCase();
		const tb = String(b?.title ?? '').toLowerCase();
		return ta.localeCompare(tb);
	});
}
